// API wrapper for the Lead Engine module (backend: lead_engine/, mounted at
// /lead-engine on the common backend - see SalesOS-Backend/main.py).
// Reuses the shared `API` axios instance from src/lib/api.js rather than a
// dedicated instance like lead-finder/api.js does, because /runs/* requires
// the app's shared JWT login (AuthContext's injectTokenGetter) - that
// interceptor already lives on `API`.
import API from "@/lib/api";

// --- Trigger + run tracking -------------------------------------------

// Starts the n8n workflow for one company. Shape matches exactly what the
// n8n Webhook node already expects (Company_list: [{ company_name,
// website_url }]) - the backend only adds `run_id` before forwarding this
// to n8n, it doesn't reshape it.
export const triggerWorkflow = ({ companyName, companyUrl }) =>
  API.post("/lead-engine/runs/trigger", {
    Company_list: [{ company_name: companyName, website_url: companyUrl }],
  }).then((res) => res.data);

// The current user's in-progress run, if any - null if they have none.
// Call this on mount so a page refresh doesn't lose the "workflow in
// progress" lock state.
export const getActiveRun = () =>
  API.get("/lead-engine/runs/active").then((res) => res.data);

export const getRun = (runId) =>
  API.get(`/lead-engine/runs/${runId}`).then((res) => res.data);

// "Stop workflow" (Startworlflow.jsx, next to the trigger form's Submit
// button) - lets the user close out their own stuck/unwanted active run
// (e.g. n8n's completion callback never arrived) instead of being locked
// out of starting a new one until someone fixes it backend-side. Only
// works on a run this user themselves triggered and that's still
// in_progress (backend enforces both - see routes/runs.py's cancel_run).
export const cancelRun = (runId) =>
  API.post(`/lead-engine/runs/${runId}/cancel`).then((res) => res.data);

// A completed run's `company_id` points here for the actual per-stage output
// (contact info, company research, generated proposal) - the run row itself
// never holds results, only trigger/lock state.
export const getCompany = (companyId) =>
  API.get(`/lead-engine/companies/${companyId}`).then((res) => res.data);

// --- Bulk CSV upload -------------------------------------------------------
// A batch is many single-company runs started together from one CSV. Each
// company still gets its own run_id under the hood (see LeadEngineBatch /
// batch-companies below), so once a batch finishes its companies show up in
// the normal history (listCompanies) like anything else - nothing special
// needed there.

// file: a File/Blob (the raw CSV, headers must be exactly "company_name" and
// "website_url"). Backend validates headers/row-count and 409s if this user
// already has an active run OR batch - same conflict rules as triggerWorkflow.
export const uploadBatch = (file) => {
  const formData = new FormData();
  formData.append("file", file);
  // `API`'s instance default is Content-Type: application/json - explicitly
  // unsetting it here (not setting it to the literal string
  // "multipart/form-data") lets the browser generate the correct header
  // itself, boundary included. A hardcoded multipart/form-data string with
  // no boundary produces a request the backend's multipart parser can't
  // actually read.
  return API.post("/lead-engine/batches", formData, {
    headers: { "Content-Type": undefined },
  }).then((res) => res.data);
};

export const getActiveBatch = () =>
  API.get("/lead-engine/batches/active").then((res) => res.data);

export const getBatch = (batchId) =>
  API.get(`/lead-engine/batches/${batchId}`).then((res) => res.data);

// Per-company breakdown for a batch's progress table - company_id/company_status
// stay null until that company's own run reaches Lead Engine's link-company
// stage, same lifecycle as a single run's company_id.
export const getBatchCompanies = (batchId) =>
  API.get(`/lead-engine/batches/${batchId}/companies`).then((res) => res.data);

// --- Notifications (TopBar bell) ------------------------------------------
// A "notification" is just a failed run this user triggered, surfaced
// through the TopBar bell rather than as a toast the user might have missed
// (see backend routes/runs.py's list_notifications) - see errors from n8n's
// Error Workflow, the stale-run watchdog, or a manual "Stop workflow", all
// the same way.

export const getNotifications = () =>
  API.get("/lead-engine/runs/notifications").then((res) => res.data);

// Clicking a notification -> { isRead: true }. Its "x" button -> { dismissed: true }.
export const updateNotification = (runId, { isRead, dismissed } = {}) =>
  API.patch(`/lead-engine/runs/${runId}/notification`, {
    is_read: isRead,
    dismissed,
  }).then((res) => res.data);

// --- Company history -----------------------------------------------------

export const listCompanies = ({ limit = 10, offset = 0, status, search } = {}) =>
  API.get("/lead-engine/companies", {
    params: { limit, offset, ...(status ? { status } : {}), ...(search ? { search } : {}) },
  }).then((res) => res.data);
